import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { LISTINGS_MANAGEMENT_DASHBOARD_QUERY_KEY, N8N_INGEST_RUNS_QUERY_KEY } from '@/config/appSettingsKeys'

export interface ListingsRefreshRun {
  id: string
  status: string
  epg_sync_status: string | null
  files_total: number
  files_succeeded: number
  files_failed: number
  files_skipped: number
  error_summary: string | null
  started_at: string | null
  finished_at: string | null
}

export const ACTIVE_REFRESH_RUN_STORAGE_KEY = 'insideaccess:activeRefreshRunId'

const TERMINAL = new Set(['completed', 'partial', 'failed'])
const IN_PROGRESS = new Set(['pending', 'running'])

/** No DB updates for this long → show stale warning and allow dismiss */
export const REFRESH_RUN_STALE_MS = 10 * 60 * 1000

/** Do not auto-resume tracking after reload if the run started longer ago than this */
export const REFRESH_RUN_RESUME_MAX_MS = 15 * 60 * 1000

export function isRefreshRunTerminal(status: string | null | undefined): boolean {
  return !!status && TERMINAL.has(status)
}

export function isRefreshRunInProgress(status: string | null | undefined): boolean {
  return !!status && IN_PROGRESS.has(status)
}

export function isRefreshRunStale(run: ListingsRefreshRun | null | undefined, maxAgeMs = REFRESH_RUN_STALE_MS): boolean {
  if (!run || isRefreshRunTerminal(run.status)) return false
  const started = run.started_at ?? run.finished_at
  if (!started) return false
  return Date.now() - new Date(started).getTime() > maxAgeMs
}

export function shouldResumeRefreshRun(
  status: string | null | undefined,
  startedAt: string | null | undefined,
  finishedAt: string | null | undefined
): boolean {
  if (!isRefreshRunInProgress(status) || finishedAt) return false
  if (!startedAt) return true
  return Date.now() - new Date(startedAt).getTime() < REFRESH_RUN_RESUME_MAX_MS
}

export function persistActiveRefreshRunId(runId: string | null, startedAt?: string | null) {
  try {
    if (runId) {
      sessionStorage.setItem(
        ACTIVE_REFRESH_RUN_STORAGE_KEY,
        JSON.stringify({ runId, startedAt: startedAt ?? new Date().toISOString() })
      )
    } else {
      sessionStorage.removeItem(ACTIVE_REFRESH_RUN_STORAGE_KEY)
    }
  } catch {
    // sessionStorage unavailable (private mode, etc.)
  }
}

export function readPersistedRefreshRunId(): string | null {
  try {
    const raw = sessionStorage.getItem(ACTIVE_REFRESH_RUN_STORAGE_KEY)
    if (!raw) return null

    try {
      const parsed = JSON.parse(raw) as { runId?: string; startedAt?: string }
      if (parsed?.runId) {
        if (parsed.startedAt && Date.now() - new Date(parsed.startedAt).getTime() > REFRESH_RUN_RESUME_MAX_MS) {
          sessionStorage.removeItem(ACTIVE_REFRESH_RUN_STORAGE_KEY)
          return null
        }
        return parsed.runId
      }
    } catch {
      // Legacy plain UUID — drop it; resume must verify against DB
      sessionStorage.removeItem(ACTIVE_REFRESH_RUN_STORAGE_KEY)
    }

    return null
  } catch {
    return null
  }
}

export async function fetchRefreshRun(runId: string): Promise<ListingsRefreshRun | null> {
  const { data, error } = await supabase
    .from('n8n_ingest_runs')
    .select(
      'id, status, epg_sync_status, files_total, files_succeeded, files_failed, files_skipped, error_summary, started_at, finished_at'
    )
    .eq('id', runId)
    .single()
  if (error) return null
  return data as ListingsRefreshRun
}

/** True only for recent, genuinely in-progress runs worth polling in the UI */
export function shouldTrackRefreshRun(run: ListingsRefreshRun | null | undefined): boolean {
  if (!run || !isRefreshRunInProgress(run.status) || run.finished_at) return false
  if (isRefreshRunStale(run, REFRESH_RUN_RESUME_MAX_MS)) return false
  return true
}

export async function abandonRefreshRun(runId: string): Promise<void> {
  const { error } = await supabase.rpc('abandon_n8n_ingest_run', { p_run_id: runId })
  if (error) throw error
}

export function useListingsRefreshRun(runId: string | null, enabled: boolean) {
  const queryClient = useQueryClient()
  const queryKey = [...N8N_INGEST_RUNS_QUERY_KEY, 'poll', runId] as const

  useEffect(() => {
    if (!enabled || !runId) return

    const channel = supabase
      .channel(`n8n-ingest-run-${runId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'n8n_ingest_runs',
          filter: `id=eq.${runId}`,
        },
        () => {
          void queryClient.invalidateQueries({ queryKey })
        }
      )
      .subscribe()

    return () => {
      void supabase.removeChannel(channel)
    }
  }, [enabled, queryClient, queryKey, runId])

  return useQuery({
    queryKey,
    queryFn: async () => {
      if (!runId) return null
      const { data, error } = await supabase
        .from('n8n_ingest_runs')
        .select(
          'id, status, epg_sync_status, files_total, files_succeeded, files_failed, files_skipped, error_summary, started_at, finished_at'
        )
        .eq('id', runId)
        .single()
      if (error) throw error
      return data as ListingsRefreshRun
    },
    enabled: enabled && !!runId,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (isRefreshRunTerminal(status)) return false
      return 4000
    },
    refetchOnWindowFocus: true,
  })
}

export const refreshRunInvalidationKeys = [
  LISTINGS_MANAGEMENT_DASHBOARD_QUERY_KEY,
  N8N_INGEST_RUNS_QUERY_KEY,
] as const
